import { create } from 'zustand';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

export interface AuthUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  isManager: boolean;
  isAdmin: boolean;
  managerLogin: string;
  supervisorChain: string[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isSessionExpired: boolean;
  isLoading: boolean;
  error: string | null;

  // Role arrays — populated on app load by useUserPermissions, used for role-checking across all pages
  programAdminOrOwnerFor: string[];
  mentorFor: { programId?: string; cohortId: string }[];
  participantIn: { programId?: string; cohortId: string; status?: string; progress: number }[];

  // Actions
  checkAuth: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  updatePermissions: (perms: {
    programAdminOrOwnerFor: string[];
    mentorFor: { programId?: string; cohortId: string }[];
    participantIn: { programId?: string; cohortId: string; status?: string; progress: number }[];
  }) => void;
  isAdminOrOwnerFor: (programId: string) => boolean;
  isMentorFor: (cohortId: string) => boolean;
  isParticipantIn: (cohortId: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isSessionExpired: false,
  isLoading: true,
  error: null,
  programAdminOrOwnerFor: [],
  mentorFor: [],
  participantIn: [],

  checkAuth: async () => {
    try {
      set({ isLoading: true, error: null });
      await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) {
        set({ isAuthenticated: false, user: null, isLoading: false });
        return;
      }

      const claims = idToken.payload;
      let supervisorChain: string[] = [];
      try {
        supervisorChain = JSON.parse((claims['supervisorChain'] as string) || '[]');
      } catch (e) {
        console.warn('Failed to parse supervisorChain claim:', e);
      }
      // Extract alias from Federate identity, or from pre-token Lambda's userAlias claim
      let userId = (claims['userAlias'] as string) || '';
      if (!userId) {
        const identities = claims['identities'];
        if (Array.isArray(identities) && identities.length > 0) {
          userId = (identities[0] as { userId?: string })?.userId || '';
        }
        if (!userId) {
          const cognitoUsername = claims['cognito:username'];
          userId = typeof cognitoUsername === 'string'
            ? cognitoUsername.split('_').slice(1).join('_')
            : '';
        }
      }

      if (!userId) {
        set({ isAuthenticated: false, user: null, isLoading: false });
        return;
      }

      const user: AuthUser = {
        userId,
        email: (claims['email'] as string) || '',
        firstName: (claims['firstName'] as string) || (claims['given_name'] as string) || '',
        lastName: (claims['lastName'] as string) || (claims['family_name'] as string) || '',
        name: `${(claims['firstName'] as string) || (claims['given_name'] as string) || ''} ${(claims['lastName'] as string) || (claims['family_name'] as string) || ''}`.trim(),
        isManager: (claims['isManager'] as string) === 'true',
        isAdmin: (claims['isAdmin'] as string) === 'true',
        managerLogin: (claims['managerLogin'] as string) || '',
        supervisorChain,
      };

      // In dev, overlay the test user identity if a local config is present.
      // The real JWT is still verified — we only swap the displayed identity
      // so the UI renders exactly what the test user would see.
      if (import.meta.env.DEV) {
        try {
          const mod = await import('../config/test-user.config');
          if (mod.testUser?.alias) {
            user.userId = mod.testUser.alias;
            user.name = mod.testUser.name;
            user.firstName = mod.testUser.name.split(' ')[0] ?? mod.testUser.alias;
            user.lastName = mod.testUser.name.split(' ').slice(1).join(' ') ?? '';
            user.email = `${mod.testUser.alias}@amazon.com`;
            // Only override isManager/isAdmin when explicitly set in the test config.
            // If omitted, the real JWT claim values are preserved.
            if (mod.testUser.isManager !== undefined) user.isManager = mod.testUser.isManager;
            if (mod.testUser.isAdmin !== undefined) user.isAdmin = mod.testUser.isAdmin;
          }
        } catch {
          // No test user config — proceed as real user
        }
      }

      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      set({ isAuthenticated: false, user: null, isLoading: false });
    }
  },

  login: async () => {
    await signInWithRedirect({ provider: { custom: 'AMZNFederateOIDC' } });
  },

  logout: async () => {
    await signOut();
    set({ user: null, isAuthenticated: false, programAdminOrOwnerFor: [], mentorFor: [], participantIn: [] });
  },

  updatePermissions: (perms) => {
    set({
      programAdminOrOwnerFor: perms.programAdminOrOwnerFor,
      mentorFor: perms.mentorFor,
      participantIn: perms.participantIn,
    });
  },

  isAdminOrOwnerFor: (programId: string) => get().programAdminOrOwnerFor.includes(programId),
  isMentorFor: (cohortId: string) => get().mentorFor.some((m) => m.cohortId === cohortId),
  isParticipantIn: (cohortId: string) => get().participantIn.some((p) => p.cohortId === cohortId),
}));

// Listen for auth events
Hub.listen('auth', async (data) => {
  const { checkAuth } = useAuthStore.getState();
  switch (data.payload.event) {
    case 'signInWithRedirect':
      await checkAuth();
      useAuthStore.setState({ isSessionExpired: false });
      break;
    case 'signInWithRedirect_failure':
      useAuthStore.setState({ error: 'Sign-in failed. Please try again.', isLoading: false });
      break;
    case 'signedOut':
      useAuthStore.setState({ user: null, isAuthenticated: false });
      break;
  }
});

/**
 * Visibility change handler — checks token expiry when the user returns to the tab.
 *
 * Covers the most common "left it open" scenarios on managed Amazon laptops:
 * - Screen lock/unlock (the primary overnight case)
 * - Switching away to another app and back
 * - Minimizing and restoring the browser
 *
 * Runs proactively before the user clicks anything, so there's no mid-action
 * redirect. If the refresh token is still valid, Amplify silently refreshes
 * the ID token and nothing visible happens. If the refresh token is also
 * expired, the user is redirected to SSO — which on a corporate Federate
 * setup is typically a silent bounce back to the app.
 */
const TOKEN_EXPIRY_BUFFER_SECONDS = 120;

document.addEventListener('visibilitychange', async () => {
  if (document.visibilityState !== 'visible') return;

  // Only run if the user is currently authenticated
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return;

  try {
    const session = await fetchAuthSession();
    const exp = session.tokens?.idToken?.payload?.exp as number | undefined;
    const nowSeconds = Math.floor(Date.now() / 1000);
    const isExpiredOrExpiring = !exp || exp === undefined || exp - nowSeconds < TOKEN_EXPIRY_BUFFER_SECONDS;

    if (!isExpiredOrExpiring) return; // Token is still fresh — nothing to do

    // Token is expired or about to expire — try a silent refresh first
    try {
      await fetchAuthSession({ forceRefresh: true });
    } catch {
      // Refresh token also expired — show session expired screen
      useAuthStore.setState({ isSessionExpired: true, isAuthenticated: false });
      return;
    }

    // Refresh succeeded — update auth state with fresh claims (best-effort, don't redirect on failure)
    try {
      const { checkAuth } = useAuthStore.getState();
      await checkAuth();
    } catch {
      // Token is valid; profile will sync on next action
    }
  } catch {
    // fetchAuthSession itself failed — session is gone
    useAuthStore.setState({ isSessionExpired: true, isAuthenticated: false });
  }
});
